<template>
  <div id="reckon-way-people" class="people-page">
    <CultureNav />
    <CultureHero
      title="Meet the Heroes"
      summary="Meet the real people behind the work and the superhero identities that celebrate the strengths they bring to the Reckon Crew."
      eyebrow="The Reckon Crew"
    />

    <main>
      <v-container>
        <section class="passport-section">
          <div class="section-heading">
            <p class="kicker">Different Strengths. One Crew.</p>
            <h2>Every team needs different strengths.</h2>
            <p>Some of us build. Some architect. Some test, automate, analyze, organize, teach, or see the thing everyone else missed.</p>
            <p>Put those abilities together and something interesting happens.</p>
          </div>

          <div class="passport-grid">
            <article
              v-for="passport in activePassports"
              :key="passport.slug"
              class="passport-card"
            >
              <div v-if="heroProfile(passport)" class="portrait-flip">
                <button
                  type="button"
                  class="portrait-flip__button"
                  :class="{ 'is-flipped': isFlipped(passport) }"
                  :aria-label="flipLabel(passport)"
                  :aria-pressed="isFlipped(passport) ? 'true' : 'false'"
                  @click="toggleCard(passport)"
                >
                  <span class="portrait-flip__face portrait-flip__face--front">
                    <img
                      :src="realImage(passport) | mediaSrc"
                      :alt="`${passport.fullName} headshot`"
                      loading="lazy"
                    />
                    <span class="passport-number">{{ passport.passportNumber }}</span>
                    <span
                      v-if="passport.chickFilAFridayTrophy"
                      class="trophy"
                      :class="`trophy--${passport.chickFilAFridayTrophy.tone}`"
                      :title="`${passport.chickFilAFridayTrophy.place} in Chick-fil-A Friday attendance`"
                    >
                      <v-icon small>mdi-trophy</v-icon>
                      {{ passport.chickFilAFridayTrophy.rank }}
                    </span>
                  </span>
                  <span class="portrait-flip__face portrait-flip__face--back">
                    <img
                      :src="heroImageFor(passport) | mediaSrc"
                      :alt="heroProfile(passport).artAlt"
                      loading="lazy"
                    />
                  </span>
                </button>
              </div>
              <div v-else class="portrait-wrap">
                <img :src="passport.headshot | mediaSrc" :alt="`${passport.fullName} headshot`" loading="lazy" />
                <span class="passport-number">{{ passport.passportNumber }}</span>
              </div>
              <div class="passport-copy">
                <p>Reckon Passport</p>
                <div class="passport-card__identity">
                  <h3>{{ passport.fullName }}</h3>
                  <a
                    v-if="passport.linkedInUrl"
                    :href="passport.linkedInUrl"
                    class="linkedin-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="`View ${passport.fullName} on LinkedIn`"
                    :title="`View ${passport.fullName} on LinkedIn`"
                  >
                    <v-icon small>mdi-linkedin</v-icon>
                  </a>
                </div>
                <span class="role">{{ passport.title }}</span>
                <dl>
                  <div>
                    <dt>Journeys</dt>
                    <dd>{{ passport.stats.reckonTrips }}</dd>
                  </div>
                  <div>
                    <dt>Badges</dt>
                    <dd>{{ passport.stats.meritBadgesEarned }}</dd>
                  </div>
                  <div>
                    <dt>Level</dt>
                    <dd>{{ passport.adventureLevel }}</dd>
                  </div>
                </dl>
                <div class="passport-card__actions">
                  <router-link
                    v-if="heroProfile(passport) && isFlipped(passport)"
                    :to="heroRoute(passport)"
                    class="card-action"
                  >
                    Origin Story <v-icon small>mdi-arrow-right</v-icon>
                  </router-link>
                  <router-link :to="passportRoute(passport)" class="card-action card-action--muted">
                    Reckon Passport <v-icon small>mdi-arrow-right</v-icon>
                  </router-link>
                </div>
              </div>
            </article>

            <article
              v-for="crewMember in pendingCrew"
              :key="crewMember.id"
              class="passport-card passport-card--pending"
            >
              <div class="portrait-wrap portrait-wrap--pending">
                <img
                  :src="crewMember.image | mediaSrc"
                  :alt="crewMember.alt"
                  loading="lazy"
                />
              </div>
              <div class="passport-copy">
                <p>New Reckon Hire</p>
                <h3>Profile Coming Soon</h3>
                <span class="role">Hero identity in development</span>
                <div class="pending-status">
                  <v-icon small>mdi-account-clock-outline</v-icon>
                  Crew profile in progress
                </div>
              </div>
            </article>
          </div>
        </section>

        <section v-if="alumniPassports.length" class="passport-section alumni-section">
          <div class="section-heading">
            <p class="kicker">Part of the Story</p>
            <h2>Crew Alumni</h2>
            <p>Their Reckon passports preserve the experiences and traditions they shared with the Crew.</p>
          </div>
          <div class="passport-grid">
            <router-link
              v-for="passport in alumniPassports"
              :key="passport.slug"
              :to="passportRoute(passport)"
              class="passport-card passport-card--alumni"
            >
              <div class="portrait-wrap">
                <img :src="passport.headshot | mediaSrc" :alt="`${passport.fullName} headshot`" loading="lazy" />
                <span class="passport-number">{{ passport.passportNumber }}</span>
              </div>
              <div class="passport-copy">
                <p>Reckon Passport · Alumni</p>
                <h3>{{ passport.fullName }}</h3>
                <span class="role">{{ passport.title }}</span>
                <dl>
                  <div><dt>Journeys</dt><dd>{{ passport.stats.reckonTrips }}</dd></div>
                  <div><dt>Badges</dt><dd>{{ passport.stats.meritBadgesEarned }}</dd></div>
                  <div><dt>Level</dt><dd>{{ passport.adventureLevel }}</dd></div>
                </dl>
                <span class="open-passport">Open Passport <v-icon small>mdi-arrow-right</v-icon></span>
              </div>
            </router-link>
          </div>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import { mediaRef } from '@/services/mediaAssets';
import AppFooter from '../components/Footer.vue';
import CultureHero from '../components/reckon-way/culture/CultureHero.vue';
import CultureNav from '../components/reckon-way/culture/CultureNav.vue';
import { heroProfileByCrewSlug } from '../data/heroProfiles';
import { passportList } from '../data/passports';

const pendingCrew = [
  {
    id: 'new-hire-female',
    image: mediaRef('heroes/coming_soon_reckon_crew.png'),
    alt: 'New Reckon crew member profile coming soon',
  },
  {
    id: 'new-hire-male',
    image: mediaRef('heroes/coming_soon_reckon_crew_male.png'),
    alt: 'New Reckon crew member profile coming soon',
  },
];

export default {
  name: 'CulturePeopleView',
  components: {
    AppFooter, CultureHero, CultureNav,
  },
  data: () => ({
    passports: passportList,
    flippedCards: {},
    pendingCrew,
  }),
  computed: {
    activePassports() {
      return this.passports.filter((passport) => passport.status === 'active');
    },
    alumniPassports() {
      return this.passports.filter((passport) => passport.status === 'alumni');
    },
  },
  methods: {
    heroProfile(passport) {
      return heroProfileByCrewSlug[passport.slug] || null;
    },
    heroRoute(passport) {
      return { name: 'hero-profile', params: { heroSlug: this.heroProfile(passport).slug } };
    },
    passportRoute(passport) {
      return { name: 'passport', params: { employeeSlug: passport.slug } };
    },
    isFlipped(passport) {
      return Boolean(this.flippedCards[passport.slug]);
    },
    toggleCard(passport) {
      this.$set(this.flippedCards, passport.slug, !this.isFlipped(passport));
    },
    flipLabel(passport) {
      return this.isFlipped(passport)
        ? `Show ${passport.fullName}'s real-world identity`
        : `Reveal ${passport.fullName}'s superhero identity, ${this.heroProfile(passport).heroName}`;
    },
    realImage(passport) {
      const profile = this.heroProfile(passport);
      return profile && profile.realImage ? profile.realImage : passport.headshot;
    },
    heroImageFor(passport) {
      const profile = this.heroProfile(passport);
      return [profile.heroImage, profile.placeholderImage];
    },
  },
};
</script>

<style scoped>
.people-page,
.people-page main {
  background: #050505;
}

.people-page main {
  padding: 64px 0 80px;
}

.passport-section + .passport-section {
  border-top: 1px solid #292929;
  margin-top: 68px;
  padding-top: 58px;
}

.section-heading {
  margin-bottom: 28px;
  max-width: 780px;
}

.kicker,
.passport-copy > p {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .76rem;
  font-weight: 900;
  letter-spacing: .04em;
  margin: 0 0 7px;
  text-transform: uppercase;
}

h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.2rem, 5vw, 3.4rem);
  line-height: 1;
  margin: 0;
}

.section-heading > p:not(.kicker) {
  color: #aaa;
  font-size: 1.05rem;
  line-height: 1.65;
  margin: 14px 0 0;
}

.passport-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.passport-card {
  background: linear-gradient(145deg, #171717, #0d0d0d);
  border: 1px solid #303030;
  border-radius: 12px;
  color: inherit;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 180ms ease, transform 180ms ease;
}

.passport-card:hover,
.passport-card:focus-within {
  border-color: #e23232;
  transform: translateY(-4px);
}

.portrait-flip {
  aspect-ratio: 3 / 4;
  background: #111;
  perspective: 1200px;
}

.portrait-flip__button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: block;
  height: 100%;
  padding: 0;
  position: relative;
  text-align: left;
  transform-style: preserve-3d;
  transition: transform 520ms cubic-bezier(.2, .7, .2, 1);
  width: 100%;
}

.portrait-flip__button.is-flipped {
  transform: rotateY(180deg);
}

.portrait-flip__button:focus-visible {
  outline: 3px solid #fff;
  outline-offset: -5px;
}

.portrait-flip__face {
  backface-visibility: hidden;
  background: #111;
  inset: 0;
  overflow: hidden;
  position: absolute;
}

.portrait-flip__face::after {
  background: linear-gradient(to top, rgba(0, 0, 0, .82), transparent 48%);
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
}

.portrait-flip__face--back {
  transform: rotateY(180deg);
}

.portrait-flip__face img {
  height: 100%;
  object-fit: contain;
  object-position: center top;
  width: 100%;
}

.portrait-wrap {
  aspect-ratio: 3 / 4;
  background: #111;
  overflow: hidden;
  position: relative;
}

.portrait-wrap::after {
  background: linear-gradient(to top, rgba(0, 0, 0, .7), transparent 45%);
  bottom: 0;
  content: '';
  inset: 0;
  position: absolute;
}

.portrait-wrap img {
  height: 100%;
  object-fit: contain;
  object-position: center top;
  width: 100%;
}

.portrait-wrap--pending::after { display: none; }

.passport-card--pending {
  border-style: dashed;
}

.pending-status {
  align-items: center;
  border-top: 1px solid #2b2b2b;
  color: #b8b8b8;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 7px;
  margin-top: 20px;
  padding-top: 15px;
  text-transform: uppercase;
}

.pending-status .v-icon { color: #e23232; }

.passport-number {
  bottom: 13px;
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .74rem;
  font-weight: 800;
  left: 15px;
  position: absolute;
  text-transform: uppercase;
  z-index: 1;
}

.trophy {
  align-items: center;
  background: #8b6b16;
  border: 1px solid rgba(255, 255, 255, .35);
  border-radius: 999px;
  color: #fff;
  display: flex;
  font-weight: 900;
  gap: 4px;
  padding: 5px 9px;
  position: absolute;
  right: 13px;
  top: 13px;
  z-index: 1;
}

.trophy .v-icon { color: inherit; }
.trophy--silver { background: #72777e; }
.trophy--bronze { background: #8a4f2a; }

.passport-copy {
  display: flex;
  flex-direction: column;
  padding: 22px;
}

.passport-copy h3 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.75rem;
  line-height: 1;
  margin: 0;
}

.passport-card__identity {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
}

.linkedin-link {
  align-items: center;
  background: #0a66c2;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 6px;
  display: inline-flex;
  flex: 0 0 auto;
  height: 30px;
  justify-content: center;
  text-decoration: none;
  transition: background-color 160ms ease, transform 160ms ease;
  width: 30px;
}

.linkedin-link .v-icon { color: #fff; }
.linkedin-link:hover {
  background: #0878d8;
  transform: translateY(-1px);
}
.linkedin-link:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
}

.role {
  color: #9e9e9e;
  display: block;
  font-size: .86rem;
  margin-top: 7px;
}

dl {
  border-bottom: 1px solid #2b2b2b;
  border-top: 1px solid #2b2b2b;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, 1fr);
  margin: 20px 0;
  padding: 15px 0;
}

dt {
  color: #777;
  font-size: .62rem;
  font-weight: 900;
  text-transform: uppercase;
}

dd {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1rem;
  margin: 3px 0 0;
}

.open-passport {
  align-items: center;
  color: #fff;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 5px;
  margin-top: auto;
  text-transform: uppercase;
}

.open-passport .v-icon { color: #e23232; }
.passport-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: auto;
}

.card-action {
  align-items: center;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: .8rem;
  font-weight: 900;
  gap: 5px;
  text-decoration: none;
  text-transform: uppercase;
}

.card-action .v-icon { color: #e23232; }
.card-action--muted { color: #b8b8b8; }
.card-action:hover { color: #e23232; }
.card-action:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 4px;
}
.passport-card--alumni { opacity: .84; }

@media (max-width: 960px) {
  .passport-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .people-page main { padding-top: 46px; }
  .passport-grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .passport-card,
  .portrait-flip__button { transition: none; }
}
</style>
